/**
 * AI router - Vercel AI SDK + OpenRouter integration + deal intelligence
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';
import { requireAiExtractionQuota } from '../middleware/feature-gate';

// Message schema
const messageSchema = z.object({
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
});

/**
 * Guard + error translation for every Anthropic-backed handler.
 *
 * Two failure modes used to surface to the athlete as a bare "internal
 * server error", because the oRPC boundary sanitizes non-ORPCError throws:
 *
 *   1. `ANTHROPIC_API_KEY` unset — `estimateDealValue` already guarded on
 *      this, but the two extraction handlers did not, so a missing key
 *      died deep inside the AI SDK with no usable message.
 *   2. Anthropic refusing the call — spend cap reached, rate limit, or a
 *      revoked key. These arrive as `AI_APICallError` whose `.message` is
 *      already human-readable ("You have reached your specified API usage
 *      limits. You will regain access on <date>"). That text is exactly
 *      what the operator needs, so pass it through rather than swallow it.
 *
 * Diagnosed 2026-07-26: extraction was 500ing solely because the Anthropic
 * account had hit its configured usage limit. The code was correct; the
 * error reporting was not.
 */
async function withAiErrorHandling<T>(fn: () => Promise<T>): Promise<T> {
  if (!process.env.ANTHROPIC_API_KEY) {
    throw new ORPCError('PRECONDITION_FAILED', {
      message: 'AI features are unavailable: ANTHROPIC_API_KEY is not configured.',
    });
  }
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ORPCError) throw err;
    const name = err instanceof Error ? err.name : '';
    const message = err instanceof Error ? err.message : String(err);
    // AI SDK upstream failures (quota, rate limit, auth) carry an operator-
    // actionable message. Surface it; anything else keeps the generic path
    // so genuine bugs still reach Sentry as INTERNAL_SERVER_ERROR.
    if (name === 'AI_APICallError' || /usage limit|rate limit|quota|credit balance/i.test(message)) {
      throw new ORPCError('PRECONDITION_FAILED', {
        message: `AI service unavailable — ${message}`,
      });
    }
    throw err;
  }
}

export const aiRouter = {
  /**
   * Chat completion with streaming
   */
  chat: protectedProcedure
    .input(
      z.object({
        messages: z.array(messageSchema),
        model: z.string().default('anthropic/claude-sonnet-4'),
      }),
    )
    .handler(async ({ input }) => {
      const { streamText, convertToCoreMessages } = await import('ai');
      const { createOpenRouter } = await import('@openrouter/ai-sdk-provider');

      const openrouter = createOpenRouter({
        apiKey: process.env.OPENROUTER_API_KEY,
      });

      const result = streamText({
        model: openrouter(input.model),
        messages: convertToCoreMessages(input.messages),
      });

      return result.textStream;
    }),

  /**
   * List available models from OpenRouter
   */
  models: protectedProcedure
    .output(
      z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          context_length: z.number(),
          pricing: z.object({
            prompt: z.string(),
            completion: z.string(),
          }),
        }),
      ),
    )
    .handler(async () => {
      const response = await fetch('https://openrouter.ai/api/v1/models', {
        headers: {
          Authorization: `Bearer ${process.env.OPENROUTER_API_KEY}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }

      const data = (await response.json()) as {
        data: Array<{
          id: string;
          name: string;
          context_length: number;
          pricing: { prompt: string; completion: string };
        }>;
      };
      return data.data.slice(0, 50);
    }),

  /**
   * Extract deal info from DM text
   */
  extractFromText: protectedProcedure
    .use(requireAiExtractionQuota())
    .input(z.object({ text: z.string() }))
    .handler(async ({ input }) =>
      withAiErrorHandling(async () => {
        const { extractDealFromText } = await import('@app/ai');
        return extractDealFromText(input.text);
      }),
    ),

  /**
   * Extract deal info from screenshot
   */
  extractFromScreenshot: protectedProcedure
    .use(requireAiExtractionQuota())
    .input(z.object({ imageUrl: z.string() }))
    .handler(async ({ input }) =>
      withAiErrorHandling(async () => {
        const { extractDealFromScreenshot } = await import('@app/ai');
        return extractDealFromScreenshot(input.imageUrl);
      }),
    ),

  /**
   * Estimate deal value with athlete context
   */
  estimateDealValue: protectedProcedure
    .use(requireAiExtractionQuota())
    .input(z.object({ dealId: z.string() }))
    .handler(async ({ context, input }) => {
      if (!process.env.ANTHROPIC_API_KEY) {
        throw new ORPCError('PRECONDITION_FAILED', { message: 'ANTHROPIC_API_KEY is not configured' });
      }
      const { db } = context;
      const { estimateDealValue } = await import('@app/ai');
      const userId = context.session.user.id;

      const deal = await db.deal.findFirst({
        where: { id: input.dealId, athleteId: userId },
        include: {
          deliverables: { select: { title: true } },
        },
      });
      if (!deal) throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });

      const user = await db.user.findUnique({
        where: { id: userId },
        select: { sport: true, school: true, socialFollowers: true, socialEngagement: true },
      });

      try {
        return await estimateDealValue(
          {
            sport: user?.sport ?? 'Unknown',
            school: user?.school ?? undefined,
            socialFollowers: user?.socialFollowers ?? undefined,
            socialEngagement: user?.socialEngagement ?? undefined,
          },
          {
            brandName: deal.brandName,
            amount: Number(deal.amount),
            deliverables: deal.deliverables.map((d: { title: string }) => d.title),
            isExclusive: deal.isExclusive,
            duration:
              deal.startDate && deal.endDate
                ? `${Math.ceil((deal.endDate.getTime() - deal.startDate.getTime()) / (1000 * 60 * 60 * 24))} days`
                : undefined,
          },
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `AI valuation failed: ${message}` });
      }
    }),

  /**
   * Summarize contract text
   */
  summarizeContract: protectedProcedure
    .input(z.object({ text: z.string() }))
    .handler(async ({ input }) => {
      const { summarizeContract } = await import('@app/ai');
      return summarizeContract(input.text);
    }),
};
